import React, { useRef, useLayoutEffect, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

const Hero = () => {
  const sectionRef = useRef();
  const stickyRef = useRef();
  const titleRef = useRef();
  const titleCharsRef = useRef([]);
  const professionRef = useRef();
  const ctaRef = useRef();
  const imagesContainerRef = useRef();
  const backgroundRef = useRef();
  const heroContentRef = useRef();
  const [currentProfession, setCurrentProfession] = useState(0);

  const professions = ["Singer", "Model", "Actress", "TV Reporter"];
  const title = "Safaa Shiey";

  // Optimisation des images basée sur les dimensions réelles
  const featuredImages = [
    {
      src: "/assets/1.jpg",
      width: 1607,
      height: 2407,
      className: "col-span-2 row-span-3",
      aspect: "portrait"
    },
    {
      src: "/assets/13.jpg",
      width: 1500,
      height: 2250,
      className: "col-span-1 row-span-2",
      aspect: "portrait"
    },
    {
      src: "/assets/4.jpg",
      width: 1526,
      height: 2285,
      className: "col-span-1 row-span-2",
      aspect: "portrait"
    },
    {
      src: "/assets/5.jpg",
      width: 1144,
      height: 1714,
      className: "col-span-1 row-span-2",
      aspect: "portrait"
    },
    {
      src: "/assets/3.jpg",
      width: 1200,
      height: 1800,
      className: "col-span-1 row-span-2",
      aspect: "portrait"
    },
    {
      src: "/assets/12.jpg",
      width: 1526,
      height: 2285,
      className: "col-span-1 row-span-2",
      aspect: "portrait"
    },
    {
      src: "/assets/2.jpg",
      width: 1920,
      height: 1080,
      className: "col-span-3 row-span-2",
      aspect: "landscape"
    },
    {
      src: "/assets/11.jpg",
      width: 1229,
      height: 1844,
      className: "col-span-2 row-span-3",
      aspect: "portrait"
    },
    {
      src: "/assets/20.jpg",
      width: 908,
      height: 1210,
      className: "col-span-1 row-span-2",
      aspect: "portrait"
    },
    {
      src: "/assets/24.jpg",
      width: 908,
      height: 1210,
      className: "col-span-1 row-span-2",
      aspect: "portrait"
    },
    {
      src: "/assets/15.jpg",
      width: 900,
      height: 1210,
      className: "col-span-1 row-span-2",
      aspect: "portrait"
    },
    {
      src: "/assets/9.jpg",
      width: 646,
      height: 716,
      className: "col-span-1 row-span-1",
      aspect: "square"
    },
    {
      src: "/assets/25.jpg",
      width: 771,
      height: 759,
      className: "col-span-1 row-span-1",
      aspect: "square"
    },
    {
      src: "/assets/22.jpg",
      width: 908,
      height: 959,
      className: "col-span-1 row-span-1",
      aspect: "square"
    },
    {
      src: "/assets/8.jpg",
      width: 804,
      height: 1204,
      className: "col-span-1 row-span-2",
      aspect: "portrait"
    },
    {
      src: "/assets/14.jpg",
      width: 804,
      height: 1204,
      className: "col-span-1 row-span-2",
      aspect: "portrait"
    },
    {
      src: "/assets/6.jpg",
      width: 850,
      height: 465,
      className: "col-span-2 row-span-1",
      aspect: "landscape"
    },
    {
      src: "/assets/17.jpg",
      width: 800,
      height: 448,
      className: "col-span-2 row-span-1",
      aspect: "landscape"
    },
    {
      src: "/assets/28.jpg",
      width: 768,
      height: 432,
      className: "col-span-2 row-span-1",
      aspect: "landscape"
    },
    {
      src: "/assets/18.jpg",
      width: 640,
      height: 497,
      className: "col-span-1 row-span-1",
      aspect: "landscape"
    },
    {
      src: "/assets/19.jpg",
      width: 747,
      height: 488,
      className: "col-span-1 row-span-1",
      aspect: "landscape"
    },
    {
      src: "/assets/23.jpg",
      width: 771,
      height: 651,
      className: "col-span-1 row-span-1",
      aspect: "landscape"
    },
    {
      src: "/assets/26.jpg",
      width: 633,
      height: 800,
      className: "col-span-1 row-span-1",
      aspect: "portrait"
    },
    {
      src: "/assets/21.jpg",
      width: 565,
      height: 701,
      className: "col-span-1 row-span-1",
      aspect: "portrait"
    },
    {
      src: "/assets/16.jpg",
      width: 640,
      height: 960,
      className: "col-span-1 row-span-2",
      aspect: "portrait"
    }
  ];

  // Animation de caractères pour le titre
  const addToCharsRef = (el) => {
    if (el && !titleCharsRef.current.includes(el)) {
      titleCharsRef.current.push(el);
    }
  };

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      // Animation du background subtil
      gsap.fromTo(backgroundRef.current,
        {
          scale: 1.1,
          opacity: 0
        },
        {
          scale: 1,
          opacity: 1,
          duration: 2,
          ease: "expo.out"
        }
      );

      // Animation des caractères du titre
      const titleChars = titleCharsRef.current;
      gsap.fromTo(titleChars,
        {
          y: 120,
          opacity: 0,
          rotationX: -90,
          transformOrigin: "50% 100%"
        },
        {
          y: 0,
          opacity: 1,
          rotationX: 0,
          duration: 1.6,
          stagger: {
            amount: 0.8,
            from: "center"
          },
          ease: "power4.out",
          delay: 0.3
        }
      );

      // Animation de la profession avec effet de blur
      gsap.fromTo(professionRef.current,
        {
          y: 60,
          opacity: 0,
          filter: "blur(10px)"
        },
        {
          y: 0,
          opacity: 1,
          filter: "blur(0px)",
          duration: 1.2,
          ease: "power3.out",
          delay: 1.2
        }
      );

      // Animation du CTA avec effet magnetic
      gsap.fromTo(ctaRef.current,
        {
          y: 40,
          opacity: 0,
          scale: 0.8
        },
        {
          y: 0,
          opacity: 1,
          scale: 1,
          duration: 1,
          ease: "elastic.out(1, 0.8)",
          delay: 1.8
        }
      );

      // Effet magnetic sur le CTA
      const magneticBtn = ctaRef.current;
      
      const handleMouseMove = (e) => {
        const { left, top, width, height } = magneticBtn.getBoundingClientRect();
        const x = e.clientX - (left + width / 2);
        const y = e.clientY - (top + height / 2);
        
        gsap.to(magneticBtn, {
          x: x * 0.3,
          y: y * 0.3,
          duration: 1,
          ease: "power2.out"
        });
      };

      const handleMouseLeave = () => {
        gsap.to(magneticBtn, {
          x: 0,
          y: 0,
          duration: 1,
          ease: "elastic.out(1, 0.8)"
        });
      };

      magneticBtn.addEventListener('mousemove', handleMouseMove);
      magneticBtn.addEventListener('mouseleave', handleMouseLeave);

      // CORRECTION : Timeline principale avec ScrollTrigger
      const mainTimeline = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top top",
          end: () => {
            const h = imagesContainerRef.current?.scrollHeight || window.innerHeight;
            return "+=" + Math.max(h, window.innerHeight * 5);
          },
          scrub: 0.5,
          pin: stickyRef.current,
          anticipatePin: 1,
          pinSpacing: true,
          invalidateOnRefresh: true,
          markers: false,
        }
      });

      // CORRECTION : Phase 1 - Faire disparaître TOUT le contenu hero ensemble
      mainTimeline
        .to(heroContentRef.current, {
          opacity: 0,
          y: -80,
          filter: "blur(10px)",
          duration: 1.5,
          ease: "power2.inOut"
        }, 0.5)

        // CORRECTION : Phase 2 - Faire apparaître la grille d'images
        .fromTo(imagesContainerRef.current, {
          opacity: 0,
          y: 100
        }, {
          opacity: 1,
          y: 0,
          duration: 2,
          ease: "power3.out"
        }, 1);

      // CORRECTION : Animations individuelles des images DANS la timeline principale
      const images = imagesContainerRef.current?.querySelectorAll('.image-item') || [];
      
      // Ajouter les animations d'images à la timeline principale
      images.forEach((image, index) => {
        mainTimeline.fromTo(image,
          {
            y: 150,
            opacity: 0,
            rotationY: index % 2 === 0 ? -15 : 15,
            scale: 0.9
          },
          {
            y: 0,
            opacity: 1,
            rotationY: 0,
            scale: 1,
            duration: 0.8,
            ease: "power3.out"
          },
          1.2 + index * 0.03 // Stagger très léger pour performance
        );
        
        // Parallax léger pour chaque image
        mainTimeline.to(image, {
          y: -80,
          ease: "none",
          duration: 2
        }, 2.5);
      });

      // CORRECTION : Effets de hover séparés (ne pas les mettre dans la timeline)
      images.forEach((image, index) => {
        const img = image.querySelector('img');
        
        // Effet de hover avancé
        const hoverTl = gsap.timeline({ paused: true });
        
        hoverTl.to(img, {
          scale: 1.1,
          duration: 0.6,
          ease: "power2.out"
        }, 0);

        hoverTl.to(image, {
          y: -15,
          duration: 0.6,
          ease: "power2.out"
        }, 0);

        hoverTl.to(image, {
          boxShadow: "0 25px 50px rgba(0,0,0,0.15)",
          duration: 0.6,
          ease: "power2.out"
        }, 0);

        image.addEventListener('mouseenter', () => hoverTl.play());
        image.addEventListener('mouseleave', () => hoverTl.reverse());
      });

      // Rafraîchir ScrollTrigger après le chargement des images
      const imgs = imagesContainerRef.current?.querySelectorAll('img') || [];
      let loaded = 0;
      const onImgLoad = () => {
        loaded += 1;
        if (loaded === imgs.length) {
          ScrollTrigger.refresh();
        }
      };
      imgs.forEach((img) => {
        if (img.complete) {
          onImgLoad();
        } else {
          img.addEventListener('load', onImgLoad);
          img.addEventListener('error', onImgLoad);
        }
      });

      // Animation de la profession qui change
      const professionChange = () => {
        const span = professionRef.current?.querySelector('span');
        if (!span) return;
        gsap.to(span, {
          y: -20,
          opacity: 0,
          duration: 0.3,
          ease: "power2.in",
          onComplete: () => {
            setCurrentProfession((prev) => (prev + 1) % professions.length);
            const newSpan = professionRef.current?.querySelector('span');
            if (!newSpan) return;
            gsap.fromTo(newSpan, {
              y: 20,
              opacity: 0
            }, {
              y: 0,
              opacity: 1,
              duration: 0.5,
              ease: "power2.out"
            });
          }
        });
      };

      // Interval pour changer la profession
      const professionInterval = setInterval(professionChange, 3000);

      return () => {
        clearInterval(professionInterval);
        magneticBtn.removeEventListener('mousemove', handleMouseMove);
        magneticBtn.removeEventListener('mouseleave', handleMouseLeave);
        imgs.forEach((img) => {
          img.removeEventListener('load', onImgLoad);
          img.removeEventListener('error', onImgLoad);
        });
      };
    });

    return () => ctx.revert();
  }, [professions.length]);

  return (
    <div ref={sectionRef} className="relative bg-white overflow-hidden" id="home">
      {/* Éléments de background animés */}
      <div ref={backgroundRef} className="absolute inset-0 opacity-0">
        <div className="absolute top-1/4 -left-20 w-96 h-96 bg-blue-50 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-pink-50 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-purple-50 rounded-full blur-3xl"></div>
      </div>

      {/* Scroll indicator amélioré */}
      <div className="fixed top-8 left-1/2 -translate-x-1/2 z-50">
        <div className="flex flex-col items-center gap-3">
          <span className="text-xs font-light text-gray-900/60 tracking-[0.3em] uppercase">
            Scroll
          </span>
          <div className="w-px h-16 bg-gradient-to-b from-gray-900/40 to-transparent relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-4 bg-gray-900/60 animate-scroll-indicator"></div>
          </div>
        </div>
      </div>

      {/* Sticky container */}
      <div 
        ref={stickyRef}
        className="relative h-screen w-full flex items-center justify-center"
      >
        {/* Contenu hero */}
        <div 
          ref={heroContentRef}
          className="absolute inset-0 flex flex-col items-center justify-center z-20 px-6 md:px-10"
        >
          {/* Titre avec animation par caractère */}
          <h1 
            ref={titleRef}
            className="text-5xl md:text-7xl lg:text-8xl xl:text-9xl font-light text-gray-900 tracking-tight text-center md:whitespace-nowrap mb-8 leading-tight"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {title.split('').map((char, index) => (
              <span
                key={index}
                ref={addToCharsRef}
                className="inline-block opacity-0"
                style={{ 
                  transform: 'translateY(120px) rotateX(-90deg)',
                  transformOrigin: '50% 100%'
                }}
              >
                {char === ' ' ? '\u00A0' : char}
              </span>
            ))}
          </h1>
          
          {/* Profession avec animation de blur */}
          <div 
            ref={professionRef}
            className="h-16 flex items-center justify-center my-6 opacity-0"
          >
            <div className="relative">
              <span className="text-xl md:text-2xl text-gray-600 font-light tracking-widest uppercase will-change-transform">
                {professions[currentProfession]}
              </span>
            </div>
          </div>

          {/* CTA avec effet magnetic */}
          <div ref={ctaRef} className="opacity-0">
            <a href="#about" className="px-8 py-4 text-gray-700 border border-gray-300 rounded-full hover:border-gray-400 transition-all duration-300 hover:shadow-lg text-base font-medium tracking-wide bg-white/80 backdrop-blur-sm inline-block">
              Learn More
            </a>
          </div>
        </div>

        {/* CORRECTION : Grid d'images - s'assurer qu'elle reste visible */}
        <div 
          ref={imagesContainerRef}
          className="absolute inset-0 flex items-center justify-center opacity-0 z-10 pt-20 pb-32"
        >
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4 auto-rows-[120px] md:auto-rows-[160px] lg:auto-rows-[200px]">
                {featuredImages.map((image, index) => (
                  <div
                    key={index}
                    className={`image-item relative overflow-hidden rounded-lg md:rounded-xl group cursor-pointer ${image.className}`}
                  >
                    <img
                      src={image.src}
                      alt={`Portfolio ${index + 1}`}
                      className="w-full h-full object-cover transition-transform duration-700"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-all duration-500"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Informations en bas */}
      <div className="fixed bottom-8 left-8 z-30">
        <div className="flex items-center space-x-3">
          <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
          <span className="text-gray-500 text-sm font-medium">London, UK</span>
        </div>
      </div>

      <div className="fixed bottom-8 right-8 z-30">
        <span className="text-gray-500 text-sm font-medium">@safaashiey</span>
      </div>

      <style jsx>{`
        @keyframes scroll-indicator {
          0% { transform: translateY(-100%); }
          50% { transform: translateY(400%); }
          100% { transform: translateY(400%); }
        }
        
        .animate-scroll-indicator {
          animation: scroll-indicator 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default Hero;